// App.js - UPDATED WITH STRIPE CUSTOMER VALIDATION
import React, { useState, useEffect, useCallback } from "react";
import { Brain } from "lucide-react";
// LandingPage is available but not currently used in this flow
// import LandingPage from "./components/LandingPage";
import WelcomeScreen from "./components/WelcomeScreen";
import PersonalInfoScreen from "./components/PersonalInfoScreen";
import QuestionScreen from "./components/QuestionScreen";
import SuccessScreen from "./components/SuccessScreen";
import { questionsConfig, categories } from "./data/assessmentData";
import "./App.css";

// Modern CSS with animations
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  /* Custom font for titles and questions */
  @font-face {
    font-family: 'P22MackinacPro';
    src: url('/P22MackinacPro-Medium_26.otf') format('opentype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }
`;

// Modern styled components
const styleSheet = document.createElement("style");
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

// Feature flag to enable/disable Stripe customer validation
const ENABLE_STRIPE_VALIDATION = false; // Set to true to enable, false to disable

const App = () => {
  const [currentScreen, setCurrentScreen] = useState("welcome");
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [currentCategory, setCurrentCategory] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [savedStatus, setSavedStatus] = useState("");
  const [hasEmailParam, setHasEmailParam] = useState(false);
  const [hasLoadedFromStorage, setHasLoadedFromStorage] = useState(false);
  const [fullReport, setFullReport] = useState(null);
  const [customerValidated, setCustomerValidated] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [unlockedDays, setUnlockedDays] = useState([true, false, false, false, false]);

  const STORAGE_KEY = "adhd_assessment_answers";

  // Initial loading animation
  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  // Fetch day unlock status from server
  useEffect(() => {
    const fetchUnlockStatus = async () => {
      try {
        const response = await fetch("/api/unlock-status");
        const data = await response.json();
        if (data.unlockedDays) {
          setUnlockedDays(data.unlockedDays);
        }
      } catch (error) {
        console.error("Failed to fetch unlock status:", error);
      }
    };
    fetchUnlockStatus();
  }, []);

  // Get email from URL params and validate with Stripe
  useEffect(() => {
    const validateStripeCustomer = async (emailAddress) => {
      try {
        setValidationMessage("Verifying access...");
        
        // Call API to check if customer exists in Stripe
        const response = await fetch('/api/check-stripe-customer', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: emailAddress }),
        });

        const data = await response.json();

        if (data.exists) {
          setCustomerValidated(true);
          setValidationMessage("");
          
          // Optional: You can store additional customer data if needed
          // For example, to personalize the experience or track subscription status
          if (data.hasActiveSubscription === false) {
            console.warn('Customer exists but has no active subscription');
            // You might want to handle this case differently
          }
        } else {
          // Customer doesn't exist in Stripe - redirect to landing page
          setValidationMessage("Invalid access. Redirecting...");
          setTimeout(() => {
            window.location.href = "https://starterkit.adhdharmony.com";
          }, 1500);
        }
      } catch (error) {
        console.error('Error validating Stripe customer:', error);
        // In case of API error, you can choose to:
        // 1. Redirect to landing page (safer option)
        setValidationMessage("Validation failed. Redirecting...");
        setTimeout(() => {
          window.location.href = "https://starterkit.adhdharmony.com";
        }, 1500);
        
        // 2. Or allow access anyway (less secure but better UX if API is down)
        // setCustomerValidated(true);
        // setValidationMessage("");
      }
    };

    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get("email");
    
    if (emailParam) {
      const decodedEmail = decodeURIComponent(emailParam);
      setEmail(decodedEmail);
      setHasEmailParam(true);
      
      // Validate the customer in Stripe only if enabled
      if (ENABLE_STRIPE_VALIDATION) {
        validateStripeCustomer(decodedEmail);
      } else {
        // Skip validation - allow access immediately
        setCustomerValidated(true);
        setValidationMessage("");
      }
    } else {
      // No email parameter - show landing page
      // hasEmailParam stays false, which will trigger LandingPage display
      setCustomerValidated(true); // Allow app to render (landing page)
    }
  }, []);

  // Update current category when question changes
  useEffect(() => {
    const categoryIndex = categories.findIndex(
      (cat) =>
        currentQuestion >= cat.startIndex && currentQuestion <= cat.endIndex
    );
    if (categoryIndex !== -1) {
      setCurrentCategory(categoryIndex);
    }
  }, [currentQuestion]);

  // Save to localStorage - including success screen state
  const saveToStorage = useCallback(
    (data, includeReport = false) => {
      try {
        const storageData = {
          answers: data,
          firstName: firstName,
          email: email,
          currentScreen: currentScreen,
          currentQuestion: currentQuestion,
        };

        // Include report data if on success screen
        if (includeReport && fullReport) {
          storageData.fullReport = fullReport;
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
        setSavedStatus("saved");
        setTimeout(() => setSavedStatus(""), 2000);
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
    },
    [firstName, email, currentScreen, currentQuestion, fullReport]
  );

  // Load from localStorage - including success screen state
  useEffect(() => {
    if (hasLoadedFromStorage || !customerValidated) return;

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsedData = JSON.parse(saved);

        // Check if this is old data
        const savedQuestionIndex = parsedData.currentQuestion || 0;
        if (
          savedQuestionIndex >= questionsConfig.length &&
          parsedData.currentScreen !== "success"
        ) {
          console.log(
            "Clearing old questionnaire data - migrating to new 15-question system"
          );
          localStorage.removeItem(STORAGE_KEY);
          setHasLoadedFromStorage(true);
          return;
        }

        // Load saved data including success screen
        if (parsedData.answers) {
          const validAnswers = {};
          Object.keys(parsedData.answers).forEach((key) => {
            const index = parseInt(key);
            if (index < questionsConfig.length) {
              validAnswers[index] = parsedData.answers[key];
            }
          });
          setAnswers(validAnswers);
        }

        setFirstName(parsedData.firstName || "");
        if (!email && parsedData.email) {
          setEmail(parsedData.email);
        }

        // Restore the saved screen including success screen
        if (parsedData.currentScreen) {
          setCurrentScreen(parsedData.currentScreen);

          // If returning to success screen, restore the report
          if (parsedData.currentScreen === "success" && parsedData.fullReport) {
            setFullReport(parsedData.fullReport);
          }
        }

        setCurrentQuestion(
          Math.min(savedQuestionIndex, questionsConfig.length - 1)
        );
      }
      setHasLoadedFromStorage(true);
    } catch (error) {
      console.error("Error loading from localStorage:", error);
      localStorage.removeItem(STORAGE_KEY);
      setHasLoadedFromStorage(true);
    }
  }, [email, customerValidated, hasLoadedFromStorage]);

  // Auto-save including success screen state
  useEffect(() => {
    if (currentScreen === "success" && fullReport) {
      // Save with report when on success screen
      saveToStorage(answers, true);
    } else if (Object.keys(answers).length > 0 || firstName) {
      // Normal save for other screens
      saveToStorage(answers);
    }
  }, [answers, firstName, currentScreen, fullReport, saveToStorage]);

  // Listen for navigation back to welcome from questions screen
  useEffect(() => {
    const handleGoToWelcome = () => {
      setCurrentScreen("welcome");
    };

    window.addEventListener("goToWelcome", handleGoToWelcome);
    return () => window.removeEventListener("goToWelcome", handleGoToWelcome);
  }, []);

  const handleStartAssessment = () => {
    // Check if user has already started (has name or answers)
    const hasStarted = firstName || Object.keys(answers).length > 0;

    if (hasStarted) {
      // Go directly to questions if they've already provided info
      setCurrentScreen("questions");
    } else {
      // Go to personal info if new user
      setCurrentScreen("personalInfo");
    }
  };

  const handlePersonalInfoSubmit = () => {
    if (!firstName.trim()) {
      alert("Please enter your first name");
      return;
    }
    if (!email.trim()) {
      alert("Email address is required");
      return;
    }
    setCurrentScreen("questions");
  };

  const handleSubmit = async (formattedData) => {
    try {
      // The report is already generated and included in formattedData
      // (QuestionScreen handles the generation and shows LoadingScreen)

      // Store the full report
      if (formattedData.report) {
        setFullReport(formattedData.report);
      }

      // Don't clear localStorage here - we want to preserve success state
      // Only clear if user starts a new assessment

      // Switch to success screen
      setCurrentScreen("success");
      return { success: true };
    } catch (error) {
      console.error("Submission error:", error);
      return {
        success: false,
        error: error.message || "Error submitting responses. Please try again.",
      };
    }
  };

  // Add a function to clear data and start fresh
  const handleStartNew = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAnswers({});
    setFirstName("");
    setCurrentQuestion(0);
    setFullReport(null);
    setCurrentScreen("personalInfo");
  };

  // Loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0E1B] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4">
            <Brain className="w-full h-full text-[#FFD700] animate-pulse" />
          </div>
          <h2 className="text-2xl font-medium text-[#FFD700]">Loading...</h2>
        </div>
      </div>
    );
  }

  // Show validation message while checking Stripe (only if validation is enabled)
  if (ENABLE_STRIPE_VALIDATION && hasEmailParam && !customerValidated && validationMessage) {
    return (
      <div className="min-h-screen bg-[#0A0E1B] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4">
            <Brain className="w-full h-full text-[#FFD700] animate-pulse" />
          </div>
          <h2 className="text-2xl font-medium text-[#FFD700]">
            {validationMessage}
          </h2>
        </div>
      </div>
    );
  }

  // If no email parameter, show landing page
  if (!hasEmailParam) {
    return <div>No access</div>;
  }

  // Wait for customer validation before showing main content (only if validation is enabled)
  if (ENABLE_STRIPE_VALIDATION && !customerValidated) {
    return (
      <div className="min-h-screen bg-[#0A0E1B] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4">
            <Brain className="w-full h-full text-[#FFD700] animate-pulse" />
          </div>
          <h2 className="text-2xl font-medium text-[#FFD700]">
            Verifying access...
          </h2>
        </div>
      </div>
    );
  }

  // Check if user has started the assessment
  const hasStarted = firstName || Object.keys(answers).length > 0;

  // Render appropriate screen
  switch (currentScreen) {
    case "success":
      return (
        <SuccessScreen
          firstName={firstName}
          email={email}
          fullReport={fullReport}
          onStartNew={handleStartNew} // Pass function to start new assessment
        />
      );

    case "personalInfo":
      return (
        <PersonalInfoScreen
          firstName={firstName}
          setFirstName={setFirstName}
          email={email}
          setEmail={setEmail}
          onSubmit={handlePersonalInfoSubmit}
        />
      );

    case "questions":
      return (
        <QuestionScreen
          firstName={firstName}
          email={email}
          answers={answers}
          setAnswers={setAnswers}
          currentQuestion={currentQuestion}
          setCurrentQuestion={setCurrentQuestion}
          currentCategory={currentCategory}
          setCurrentCategory={setCurrentCategory}
          savedStatus={savedStatus}
          onSubmit={handleSubmit}
          unlockedDays={unlockedDays}
        />
      );

    case "welcome":
    default:
      return (
        <WelcomeScreen
          onStart={handleStartAssessment}
          hasStarted={hasStarted}
        />
      );
  }
};

export default App;
